import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../common/Button';
import type { CreateUserInput } from '../../types/admin-user.types';
import type { Role } from '../../types/auth.types';

interface CreateAccountModalProps {
  onClose: () => void;
  onConfirm: (input: CreateUserInput) => Promise<void>;
}

export function CreateAccountModal({ onClose, onConfirm }: CreateAccountModalProps): React.ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('USER');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) { setError('Email or username is required.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      await onConfirm({ email: email.trim(), password, name: name.trim() || undefined, role });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account.');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="create-account-modal-title">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md glass-card p-6 animate-slide-in">
        <h2 id="create-account-modal-title" className="text-lg font-bold text-white mb-5">
          Create New Account
        </h2>

        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="create-account-email" className="block text-xs font-medium text-slate-400 mb-1">
              Email / Username <span className="text-red-400">*</span>
            </label>
            <input
              id="create-account-email"
              type="text"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="user@example.com or admin"
              autoComplete="off"
              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label htmlFor="create-account-name" className="block text-xs font-medium text-slate-400 mb-1">
              Display Name
            </label>
            <input
              id="create-account-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Optional display name"
              autoComplete="off"
              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label htmlFor="create-account-password" className="block text-xs font-medium text-slate-400 mb-1">
              Password <span className="text-red-400">*</span>
            </label>
            <input
              id="create-account-password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              autoComplete="new-password"
              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label htmlFor="create-account-role" className="block text-xs font-medium text-slate-400 mb-1">
              Role
            </label>
            <select
              id="create-account-role"
              value={role}
              onChange={e => setRole(e.target.value as Role)}
              className="w-full px-3 py-2 rounded-xl bg-surface-800 border border-white/15 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="USER">USER — Standard learner account</option>
              <option value="ADMIN">ADMIN — Administrator account</option>
            </select>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              id="create-account-cancel-btn"
              type="button"
              variant="ghost"
              className="flex-1"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              id="create-account-submit-btn"
              type="submit"
              variant="primary"
              className="flex-1"
              loading={loading}
            >
              Create Account
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
